import { css } from "@emotion/react";
import { useMessage } from "@hermes/intl";
import { useChildrenAgeOptions } from "@hermes/shared";
import {
  FilterOutlinedButton,
  FilterSearchButton,
  Select,
  useFormField,
  useScreenDetector,
  useSelectState,
} from "@hermes/ui";
import { match } from "@hermes/utils";

import type { DoctorsListParams } from "./types";

export const ChildrenAgeFilter = ({
  params,
  onParamsChange,
  variant: variantOverride,
}: {
  params: DoctorsListParams;
  onParamsChange: (params: DoctorsListParams) => void;
  variant?: "filter-search" | "filter-outlined";
}) => {
  const { isMobile, isDesktop } = useScreenDetector();
  const message = useMessage();

  const options = useChildrenAgeOptions();

  const field = useFormField(
    useSelectState<number>({
      value: params.child_age ?? null,
      parent: params.child_age,
      onValueChanged(value) {
        onParamsChange({
          child_age: value ?? undefined,
        });
      },
    }),
  );

  const selectedOption = options.find((option) => option.value === field.value);

  const variant = variantOverride
    ? variantOverride
    : isMobile
      ? "filter-search"
      : "filter-outlined";
  const props = {
    text:
      selectedOption?.label ??
      message({ id: "e509736b-e018-4da6-8dd5-c3fadbce0956" }),
    active: field.value !== null,
    styles: isDesktop ? css({ minWidth: 280 }) : undefined,
    onClear: () => {
      field.onChange({ value: null });
    },
    clearable: true,
  };

  return (
    <Select
      field={field}
      options={options}
      mobile={{
        title: message({
          id: "e509736b-e018-4da6-8dd5-c3fadbce0956",
          default: "Specify the age of the child",
        }),
      }}
    >
      {({ triggerRef, triggerProps }) =>
        match(variant, {
          "filter-outlined": () => (
            <FilterOutlinedButton.EmphasizedDropdown
              triggerRef={triggerRef}
              triggerProps={triggerProps}
              {...props}
            />
          ),
          "filter-search": () => (
            <FilterSearchButton.EmphasizedDropdown
              triggerRef={triggerRef}
              triggerProps={triggerProps}
              {...props}
            />
          ),
        })
      }
    </Select>
  );
};
